import { Controller } from "@hotwired/stimulus";
import DataTable, { Api, Config, ConfigColumns } from "datatables.net";
import "datatables.net-columncontrol-dt";
import "datatables.net-buttons-dt";
import "datatables.net-fixedcolumns";
import "datatables.net-buttons/js/buttons.html5.mjs";
import JSZip from "jszip";
import { DateTime } from "luxon";

declare global {
  interface Window {
    JSZip: typeof JSZip;
  }
}

export default class extends Controller {
  static readonly targets = ["dtTable"];
  static readonly values = {
    columnWidths: Array,
    exportable: Boolean,
    exportFileName: {
      type: String,
      default: `Inspection_Round_${new Date().toISOString().slice(0, 10)}`,
    },
    exportFormatTextColumns: Array,
    order: Array,
  };

  declare readonly hasDtTableTarget: boolean;
  declare readonly dtTableTarget: HTMLTableElement;

  declare readonly columnWidthsValue: string[];
  declare readonly hasColumnWidthsValue: boolean;
  declare readonly exportableValue: boolean;
  declare readonly hasExportableValue: boolean;
  declare readonly exportFileNameValue: string;
  declare readonly hasExportFormatTextColumnsValue: boolean;
  declare readonly exportFormatTextColumnsValue: {
    column: number;
    type: string;
  }[];
  declare readonly hasOrderValue: boolean;
  declare readonly orderValue: [number, "" | "asc" | "desc"][];

  private _dtTableApi: Api | null = null;

  connect() {
    if (!this.hasDtTableTarget) return;

    window.JSZip = JSZip;

    const config = this._buildDataTableConfig();

    this._dtTableApi = new DataTable(this.dtTableTarget, config);
  }

  disconnect() {
    this._dtTableApi?.destroy();
  }

  exportExcel() {
    if (this._dtTableApi && this.hasExportableValue && this.exportableValue) {
      this._dtTableApi.button(".buttons-excel").trigger();
    }
  }

  private _buildDataTableConfig(): Config {
    const columns = this.hasColumnWidthsValue
      ? this.columnWidthsValue.map((width): ConfigColumns => ({ width }))
      : undefined;

    const config: Config = {
      fixedColumns: { start: 1 },
      autoWidth: !this.hasColumnWidthsValue,
      paging: false,
      scrollX: true,
      layout: {
        topEnd: null,
      },
      searching: true,
      columnControl: ["searchDropdown"],
      columns: columns,
      order: this.hasOrderValue ? this.orderValue : [],
      columnDefs: [
        {
          targets: ".date-sort",
          render: (data, type) => {
            if (type !== "sort") return data;

            if (typeof data !== "string" || !data) {
              return 0;
            }

            const date = DateTime.fromFormat(data.trim(), "dd/MM/yyyy");
            return date.isValid ? date.toMillis() : 0;
          },
        },
        {
          targets: ".datetime-sort",
          render: (data, type) => {
            if (type !== "sort") return data;

            if (typeof data !== "string" || !data) {
              return 0;
            }

            const date = DateTime.fromFormat(data.trim(), "dd/MM/yyyy | hh:mm");
            return date.isValid ? date.toMillis() : 0;
          },
        },
      ],
    };

    if (this.hasExportableValue && this.exportableValue) {
      config.layout = {
        ...config.layout,
        topStart: {
          buttons: [
            {
              extend: "excelHtml5",
              title: this.exportFileNameValue,
              className: "d-none",
              exportOptions: {
                format: {
                  body: (
                    _content: string,
                    _row: number,
                    _column: number,
                    _node: HTMLElement
                  ) => {
                    if (this.hasExportFormatTextColumnsValue) {
                      const formatter = this._transformExportFormat(
                        this.exportFormatTextColumnsValue,
                        _column
                      );
                      if (formatter) {
                        return formatter(_node);
                      }
                    }

                    return _node.textContent.trim();
                  },
                },
              },
            },
          ],
        },
      };
    }

    return config;
  }

  private _transformExportFormat(
    formatters: { column: number; type: string }[],
    currentColumn: number
  ) {
    const formatter = formatters.find((c) => c.column == currentColumn);
    if (formatter?.type == "joinBadges") {
      return this._joinBadgeTexts;
    }

    return undefined;
  }

  private _joinBadgeTexts(node: HTMLElement) {
    return Array.from(node.querySelectorAll(".badge"))
      .map((badge) => badge.textContent.trim())
      .join(", ");
  }
}
